import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import { UpgradeBanner } from '../components/ui/UpgradeBanner'
import { TableSkeleton } from '../components/ui/Skeletons'
import { EmptyState } from '../components/ui/EmptyState'
import { useToast } from '../components/ui/Toast'
import { KBArticleView } from '../components/kb/KBArticleView'
import { KBArticleEditor } from '../components/kb/KBArticleEditor'
import { KBEditorV2 } from '../components/kb/editor/KBEditorV2'
import { KBCategoryBrowser } from '../components/kb/KBCategoryBrowser'
import { Button } from '../components/ui/Button'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

interface KBArticleListItem {
  id: string
  slug: string
  title: string
  article_type: string
  visibility: string
  status: string
  category_id: string | null
  category_name: string | null
  view_count: number
  tags: string | null
  updated_at: string
  created_by: string | null
}

interface KBCategoryItem {
  id: string
  name: string
  slug: string
  parent_id: string | null
  article_count: number
  children?: KBCategoryItem[]
}

const TYPE_LABELS: Record<string, string> = {
  sop: 'SOP', site_doc: 'Site Doc', product_guide: 'Product Guide',
  troubleshooting: 'Troubleshooting', client_facing: 'Client FAQ',
  faq: 'FAQ', onboarding: 'Onboarding',
}

const VISIBILITY_ICONS: Record<string, string> = {
  internal: '\u{1F512}', client: '\u{1F465}', public: '\u{1F310}', restricted: '\u{1F6AB}',
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', in_review: 'In Review', published: 'Published', archived: 'Archived',
}

type ViewMode = 'list' | 'view' | 'edit' | 'create'

export default function KnowledgeBase() {
  const navigate = useNavigate()
  const { enabled, loading: flagLoading, requiredPlan } = useFeatureFlag('kb')
  const [articles, setArticles] = useState<KBArticleListItem[]>([])
  const [categories, setCategories] = useState<KBCategoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterStatus, setFilterStatus] = useState('')
  const [filterCategory, setFilterCategory] = useState('')
  const [viewMode, setViewMode] = useState<ViewMode>('list')
  const [activeSlug, setActiveSlug] = useState<string | null>(null)
  const searchTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { toastError } = useToast()

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current)
    searchTimer.current = setTimeout(() => setDebouncedSearch(search), 300)
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current) }
  }, [search])

  const loadArticles = useCallback(async () => {
    try {
      const params = new URLSearchParams()
      if (debouncedSearch) params.set('search', debouncedSearch)
      if (filterType) params.set('article_type', filterType)
      if (filterStatus) params.set('status', filterStatus)
      if (filterCategory) params.set('category_id', filterCategory)
      const res = await api.get<{ data: KBArticleListItem[]; total: number }>(`/kb/articles?${params}`)
      setArticles(res.data)
      setTotal(res.total)
    } catch (err) {
      toastError('Failed to load articles', err)
    } finally {
      setLoading(false)
    }
  }, [debouncedSearch, filterType, filterStatus, filterCategory, toastError])

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get<KBCategoryItem[]>('/kb/categories')
      setCategories(res)
    } catch { /* non-critical */ }
  }, [])

  useEffect(() => {
    if (enabled) {
      loadArticles()
      loadCategories()
    }
  }, [enabled, loadArticles, loadCategories])

  const handleViewArticle = (slug: string) => {
    navigate(`/kb/${slug}`)
  }

  const handleEditArticle = (slug: string) => {
    navigate(`/kb/${slug}/edit`)
  }

  const handleBack = () => {
    setViewMode('list')
    setActiveSlug(null)
    loadArticles()
  }

  const handleCategoryClick = (categoryId: string) => {
    setFilterCategory(categoryId)
    setViewMode('list')
    setActiveSlug(null)
  }

  if (flagLoading) return <TableSkeleton cols={5} />
  if (!enabled) return <UpgradeBanner feature="Knowledge Base" plan={requiredPlan} description="Create SOPs, site documentation, troubleshooting guides, and client-facing articles. AI-powered search and auto-documentation coming soon." />

  // View mode — reading view
  if (viewMode === 'view' && activeSlug) {
    return (
      <KBArticleView
        slug={activeSlug}
        onBack={handleBack}
        onEdit={() => setViewMode('edit')}
        onCategoryClick={handleCategoryClick}
      />
    )
  }

  // Edit / Create mode — use V2 TipTap editor
  if (viewMode === 'edit' || viewMode === 'create') {
    return (
      <KBEditorV2
        articleSlug={viewMode === 'edit' ? activeSlug : null}
        categories={categories}
        onClose={handleBack}
        onSaved={() => loadArticles()}
      />
    )
  }

  // List mode
  return (
    <div className="page-enter">
      <div className="page-header">
        <h1 className="page-title">Knowledge Base</h1>
        <Button icon={PlusIcon} onClick={() => setViewMode('create')}>New Article</Button>
      </div>

      <div style={{ display: 'flex', gap: 24 }}>
        {/* Left sidebar - categories */}
        <div style={{ width: 220, flexShrink: 0 }} className="hidden sm:block">
          <KBCategoryBrowser
            categories={categories}
            selectedId={filterCategory}
            onSelect={setFilterCategory}
            onRefresh={loadCategories}
          />
        </div>

        {/* Main content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <input
              type="text" className="v3-input" placeholder="Search articles..."
              value={search} onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, minWidth: 200 }}
            />
            <div className="v3-select-wrap" style={{ width: 150 }}>
              <select className="v3-select" value={filterType} onChange={e => setFilterType(e.target.value)}>
                <option value="">All Types</option>
                {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
            <div className="v3-select-wrap" style={{ width: 130 }}>
              <select className="v3-select" value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="in_review">In Review</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
            {/* Mobile category selector */}
            <div className="v3-select-wrap sm:hidden" style={{ width: 140 }}>
              <select
                className="v3-select"
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
              >
                <option value="">All Categories</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
              <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
            </div>
          </div>

          {/* Stats bar */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 16, fontSize: 13, color: 'var(--text-tertiary)' }}>
            <span>{total} article{total !== 1 ? 's' : ''}</span>
          </div>

          {loading ? <TableSkeleton cols={5} /> : articles.length === 0 ? (
            <EmptyState
              title="No articles yet"
              description="Create your first knowledge base article to get started."
              action={{ label: 'New Article', onClick: () => setViewMode('create') }}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {articles.map(article => {
                const tags = article.tags ? article.tags.split(',').map(t => t.trim()).filter(Boolean) : []

                return (
                  <div
                    key={article.id}
                    className="kb-article-card"
                    onClick={() => handleViewArticle(article.slug)}
                  >
                    {/* Row 1: Title + status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span className="kb-article-card-title" style={{ flex: 1 }}>{article.title}</span>
                      <span className={`kb-type-badge kb-type-${article.article_type}`}>
                        {TYPE_LABELS[article.article_type] || article.article_type}
                      </span>
                    </div>
                    {/* Row 2: Meta */}
                    <div className="kb-article-card-meta">
                      <span className={`kb-status kb-status-${article.status}`}>
                        <span className="kb-status-dot" />
                        {STATUS_LABELS[article.status] || article.status}
                      </span>
                      {article.category_name && (
                        <>
                          <span style={{ color: 'var(--border-default)' }}>&middot;</span>
                          <span
                            className="kb-category-link"
                            onClick={e => { e.stopPropagation(); if (article.category_id) setFilterCategory(article.category_id) }}
                          >
                            {article.category_name}
                          </span>
                        </>
                      )}
                      <span style={{ color: 'var(--border-default)' }}>&middot;</span>
                      <span className="kb-visibility">
                        {VISIBILITY_ICONS[article.visibility]} {article.visibility}
                      </span>
                      <span style={{ color: 'var(--border-default)' }}>&middot;</span>
                      <span>Updated {new Date(article.updated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span style={{ color: 'var(--border-default)' }}>&middot;</span>
                      <span>{article.view_count} views</span>
                    </div>
                    {/* Row 3: Tags */}
                    {tags.length > 0 && (
                      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 2 }}>
                        {tags.slice(0, 5).map(tag => <span key={tag} className="kb-tag">{tag}</span>)}
                        {tags.length > 5 && <span className="kb-tag">+{tags.length - 5}</span>}
                      </div>
                    )}
                    {/* Row 4: Actions */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => { e.stopPropagation(); handleViewArticle(article.slug) }}
                      >
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={e => { e.stopPropagation(); handleEditArticle(article.slug) }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
